/* SpotBounty FAQ — Phase 0. Consistent with Terms and Privacy. */
import React, { useState } from "react";
import WebLayout from "../components/WebLayout";
import Seo from "../components/Seo";
import { Plus, Minus } from "lucide-react";

const FAQ = [
  {
    q: "What is SpotBounty?",
    a: "SpotBounty is a technology platform being built to connect three groups: Scouts who identify promising vending locations, Businesses that want vending service at their location, and independent vending Operators. During Phase 0 we're collecting preregistrations to validate demand and plan the initial marketplace.",
  },
  {
    q: "Is SpotBounty live yet?",
    a: "Not yet. We're in a Phase 0 preregistration and early-access period. There is no live marketplace, no live Scout bounty program, no live Creator commission program, and no live paid transactions between businesses and vending operators. If we launch, we'll notify preregistrants and publish updated program terms at that time.",
  },
  {
    q: "Is preregistration free?",
    a: "Yes. Joining any of our four Phase 0 waitlists — Scouts, Operators, Businesses, or Creators — is free. There is no purchase, subscription, or paid tier during Phase 0.",
  },
  {
    q: "Can Scouts earn money right now?",
    a: "No. Preregistering as a Scout does not earn a bounty and there is no active Scout payout in Phase 0. Any dollar figures shown on the site describe the planned Scout bounty structure at the time of publication; they are illustrative and are not guaranteed income.",
  },
  {
    q: "How much can a Scout earn?",
    a: "Bounties will vary. Our current marketing headline is \"Up to $450\" per verified location that sells; this figure describes the top of the currently planned tier, not typical earnings, and is not a projection of what any particular Scout will earn. Actual earnings, if any, will depend on future qualification, verification, a successful qualifying sale, and the Scout program terms in effect when the bounty program is live. Program details may change before launch.",
  },
  {
    q: "Does submitting a form guarantee I'll receive a bounty, a match, or a listing?",
    a: "No. Preregistration is an expression of interest. It does not guarantee that any submitted information will qualify, that any Scout will be paid, that any Operator will receive leads, that any Business will be matched with an Operator, that any Creator will earn commission, or that SpotBounty will launch service in any specific area or timeframe.",
  },
  {
    q: "Does Operator preregistration guarantee locations, revenue, or exclusivity?",
    a: "No. Independent vending operators who preregister are indicating interest. Nothing about Phase 0 preregistration guarantees leads, opportunities, territory, exclusivity, or revenue. Independent operators remain solely responsible for their own licensing, permits, insurance, equipment, installation, and business operations.",
  },
  {
    q: "Does a Business request guarantee vending service?",
    a: "No. Submitting a Business request tells us where vending demand exists. It does not guarantee that an operator will be introduced, that equipment will be installed, or that any specific service level or timeframe will be met.",
  },
  {
    q: "Does Creator registration guarantee commissions or a paid partnership?",
    a: "No. Preregistering as a Creator does not create a paid relationship with SpotBounty. Any future Creator commission or affiliate program will be governed by separate program terms introduced at that time.",
  },
  {
    q: "What does my pre-launch referral code do?",
    a: "A referral code preserves attribution — an internal record of who referred whom. Attribution during Phase 0 is not cash, not stored value, not a guaranteed commission, and not a promise of eligibility in any future paid referral program. If a paid referral program launches, additional program terms will apply.",
  },
  {
    q: "Does SpotBounty own or operate the vending machines?",
    a: "No. SpotBounty is a technology platform. It does not own, install, service, restock, or operate vending machines. Vending operations are performed by independent operators.",
  },
  {
    q: "Who sees my information during Phase 0?",
    a: "Your information is processed by SpotBounty and by our service providers (database hosting, application hosting, transactional email, anti-abuse verification, and product analytics — see the Privacy Policy for the current list). During Phase 0 Business preregistrations are not shared with Operators as active marketplace inventory. We do not sell personal information.",
  },
  {
    q: "Can I request deletion or correction of my information?",
    a: "Yes. Contact us through the /contact form to request access, correction, or deletion of your preregistration record. We honor rights required by applicable law and will respond within a reasonable time, subject to any legal, security, or fraud-prevention obligations that require us to retain certain information.",
  },
  {
    q: "What emails will I receive?",
    a: "During Phase 0 you will receive service-related and preregistration communications (for example, a confirmation email when you submit a form, and any operational message that materially affects your preregistration). We do not send promotional email during Phase 0. If we introduce promotional or marketing email in the future, we will do so under legally required consent and opt-out mechanisms.",
  },
  {
    q: "Is SpotBounty for people under 18?",
    a: "No. SpotBounty is intended for adults age 18 and over. We do not knowingly solicit or collect information from children under 18. If a child has submitted information, contact us through the /contact form and we will delete it.",
  },
  {
    q: "Can I sell my existing vending location or route through SpotBounty?",
    a: "SpotBounty is collecting seller interest from people who already own or operate vending locations, machines, or routes and may want to sell. Submitting the seller form does not create a listing or guarantee a sale. Financial figures you share are seller-provided and not independently verified. We'll contact seller-list members as we open marketplace access.",
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return (
    <WebLayout>
      <Seo title="FAQ" description="SpotBounty Phase 0 FAQ — preregistration, marketplace scope, referrals, and privacy." path="/faq" jsonLd={jsonLd} />
      <section className="max-w-3xl mx-auto px-6 py-16">
        <div className="sb-eyebrow">Help Center</div>
        <h1 className="sb-headline text-[40px] md:text-[52px] mt-3">FREQUENTLY ASKED QUESTIONS</h1>
        <p className="mt-4 text-[#A1A1AA] max-w-xl">Straight answers about SpotBounty and our Phase 0 preregistration period.</p>
        <div className="mt-10 divide-y divide-[#2A2F34] sb-card p-0">
          {FAQ.map((item, i) => {
            const isOpen = open === i;
            return (
              <button
                key={i}
                onClick={() => setOpen(isOpen ? -1 : i)}
                data-testid={`faq-item-${i}`}
                className="w-full text-left p-5 flex items-start gap-4"
              >
                <span className={`mt-1 shrink-0 w-6 h-6 rounded-full border ${isOpen ? "border-[#FFC107] text-[#FFC107]" : "border-[#2A2F34] text-[#A1A1AA]"} flex items-center justify-center`}>
                  {isOpen ? <Minus size={14} /> : <Plus size={14} />}
                </span>
                <span className="flex-1">
                  <div className="font-bold text-[15px]">{item.q}</div>
                  {isOpen && <div className="mt-3 text-[13.5px] text-[#A1A1AA] leading-relaxed">{item.a}</div>}
                </span>
              </button>
            );
          })}
        </div>
      </section>
    </WebLayout>
  );
}
